import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Play, Pause, Volume2, VolumeX, Maximize, Minimize,
  SkipBack, SkipForward, Settings, Check, Lock, ChevronRight
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format time helper
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const VideoPlayer = ({ module, modules, currentIndex, onModuleChange, onProgressUpdate }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const progressRef = useRef(null);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showPlaylist, setShowPlaylist] = useState(true);
  const [buffered, setBuffered] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showSettings, setShowSettings] = useState(false);
  
  const controlsTimeout = useRef(null);
  const lastProgressUpdate = useRef(0);

  // Video source - use placeholder or Vimeo embed
  const videoSrc = module?.vimeo_video_id 
    ? `https://player.vimeo.com/video/${module.vimeo_video_id}`
    : 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4';

  // Update progress to backend
  const saveProgress = useCallback(async (time, completed = false) => {
    if (!module?.module_id) return;
    
    // Only update every 10 seconds to reduce API calls
    if (!completed && Math.abs(time - lastProgressUpdate.current) < 10) return;
    lastProgressUpdate.current = time;
    
    try {
      await axios.post(`${API}/courses/progress`, {
        module_id: module.module_id,
        watched_seconds: Math.floor(time),
        is_completed: completed
      }, { withCredentials: true });
      
      if (onProgressUpdate) {
        onProgressUpdate(module.module_id, time, completed);
      }
    } catch (error) {
      console.error('Failed to save progress:', error);
    }
  }, [module?.module_id, onProgressUpdate]);

  // Play/Pause
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };

  // Mute/Unmute
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  // Volume change
  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      setIsMuted(newVolume === 0);
    }
  };

  // Seek
  const handleSeek = (e) => {
    const rect = progressRef.current.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  // Fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Skip forward/backward
  const skip = (seconds) => {
    if (videoRef.current) {
      videoRef.current.currentTime += seconds;
    }
  };

  // Playback rate
  const changePlaybackRate = (rate) => {
    setPlaybackRate(rate);
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
    }
    setShowSettings(false);
  };

  // Mouse activity for controls
  const handleMouseMove = () => {
    setShowControls(true);
    clearTimeout(controlsTimeout.current);
    controlsTimeout.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 3000);
  };

  // Video events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      saveProgress(video.currentTime);
    };
    const onLoadedMetadata = () => setDuration(video.duration);
    const onProgress = () => {
      if (video.buffered.length > 0) {
        setBuffered(video.buffered.end(video.buffered.length - 1));
      }
    };
    const onEnded = () => {
      setIsPlaying(false);
      saveProgress(video.duration, true);
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('progress', onProgress);
    video.addEventListener('ended', onEnded);

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('progress', onProgress);
      video.removeEventListener('ended', onEnded);
    };
  }, [saveProgress]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT') return;
      
      switch (e.key) {
        case ' ':
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft':
          skip(-10);
          break;
        case 'ArrowRight':
          skip(10);
          break;
        case 'ArrowUp':
          setVolume(v => Math.min(1, v + 0.1));
          break;
        case 'ArrowDown':
          setVolume(v => Math.max(0, v - 0.1));
          break;
        case 'm':
          toggleMute();
          break;
        case 'f':
          toggleFullscreen();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="flex gap-6">
      {/* Main Video Player */}
      <div className="flex-1">
        <div
          ref={containerRef}
          className="relative bg-black rounded-2xl overflow-hidden group"
          onMouseMove={handleMouseMove}
          onMouseLeave={() => isPlaying && setShowControls(false)}
        >
          {/* Video */}
          {module?.vimeo_video_id ? (
            <iframe
              src={`https://player.vimeo.com/video/${module.vimeo_video_id}?badge=0&autopause=0&player_id=0&app_id=58479`}
              className="w-full aspect-video"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title={module.title}
            />
          ) : (
            <video
              ref={videoRef}
              src={videoSrc}
              className="w-full aspect-video"
              onClick={togglePlay}
              poster="https://horizons-cdn.hostinger.com/18a1a0ab-885d-4c22-add1-e6337891edf7/a13f51a2c0be45427a7d8ab30aeff9bd.jpg"
            />
          )}

          {/* Play Button Overlay */}
          {!isPlaying && !module?.vimeo_video_id && (
            <div
              className="absolute inset-0 flex items-center justify-center cursor-pointer"
              onClick={togglePlay}
            >
              <div className="w-20 h-20 bg-[#d4a33a] rounded-full flex items-center justify-center hover:scale-110 transition-transform shadow-2xl">
                <Play className="w-10 h-10 text-white fill-white ml-1" />
              </div>
            </div>
          )}

          {/* Controls */}
          {!module?.vimeo_video_id && (
            <div
              className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-4 transition-opacity duration-300 ${
                showControls ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {/* Progress Bar */}
              <div
                ref={progressRef}
                className="relative h-1.5 bg-white/30 rounded-full cursor-pointer mb-4 group/progress"
                onClick={handleSeek}
              >
                {/* Buffered */}
                <div
                  className="absolute h-full bg-white/40 rounded-full"
                  style={{ width: `${(buffered / duration) * 100}%` }}
                />
                {/* Progress */}
                <div
                  className="absolute h-full bg-[#d4a33a] rounded-full"
                  style={{ width: `${(currentTime / duration) * 100}%` }}
                />
                {/* Thumb */}
                <div
                  className="absolute w-4 h-4 bg-[#d4a33a] rounded-full -top-1.5 -ml-2 opacity-0 group-hover/progress:opacity-100 transition-opacity shadow-lg"
                  style={{ left: `${(currentTime / duration) * 100}%` }}
                />
              </div>

              {/* Controls Row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {/* Play/Pause */}
                  <button onClick={togglePlay} className="text-white hover:text-[#d4a33a] transition-colors">
                    {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                  </button>

                  {/* Skip */}
                  <button onClick={() => skip(-10)} className="text-white hover:text-[#d4a33a] transition-colors">
                    <SkipBack className="w-5 h-5" />
                  </button>
                  <button onClick={() => skip(10)} className="text-white hover:text-[#d4a33a] transition-colors">
                    <SkipForward className="w-5 h-5" />
                  </button>

                  {/* Volume */}
                  <div className="flex items-center gap-2 group/volume">
                    <button onClick={toggleMute} className="text-white hover:text-[#d4a33a] transition-colors">
                      {isMuted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                    </button>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={isMuted ? 0 : volume}
                      onChange={handleVolumeChange}
                      className="w-20 h-1 bg-white/30 rounded-full appearance-none cursor-pointer opacity-0 group-hover/volume:opacity-100 transition-opacity"
                    />
                  </div>

                  {/* Time */}
                  <span className="text-white text-sm">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                </div>

                <div className="flex items-center gap-4">
                  {/* Settings */}
                  <div className="relative">
                    <button
                      onClick={() => setShowSettings(!showSettings)}
                      className="text-white hover:text-[#d4a33a] transition-colors"
                    >
                      <Settings className="w-5 h-5" />
                    </button>
                    {showSettings && (
                      <div className="absolute bottom-full right-0 mb-2 bg-[#232836] rounded-lg shadow-xl p-2 min-w-32">
                        <p className="text-gray-400 text-xs px-3 py-1">Playback Speed</p>
                        {[0.5, 0.75, 1, 1.25, 1.5, 2].map(rate => (
                          <button
                            key={rate}
                            onClick={() => changePlaybackRate(rate)}
                            className={`w-full text-left px-3 py-1 text-sm rounded flex items-center justify-between ${
                              playbackRate === rate ? 'text-[#d4a33a]' : 'text-white hover:bg-white/10'
                            }`}
                          >
                            {rate}x
                            {playbackRate === rate && <Check className="w-4 h-4" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Fullscreen */}
                  <button onClick={toggleFullscreen} className="text-white hover:text-[#d4a33a] transition-colors">
                    {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Video Title */}
        <div className="mt-4">
          <h2 className="text-2xl font-bold text-white">{module?.title}</h2>
          <p className="text-gray-400 mt-2">{module?.description}</p>
        </div>
      </div>

      {/* Playlist Sidebar */}
      {showPlaylist && (
        <div className="w-80 bg-[#232836] rounded-2xl p-4 max-h-[600px] overflow-y-auto">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center justify-between">
            Course Playlist
            <button
              onClick={() => setShowPlaylist(false)}
              className="text-gray-400 hover:text-white"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </h3>
          <div className="space-y-2">
            {modules?.map((mod, index) => {
              const isActive = mod.module_id === module?.module_id;
              const isLocked = mod.is_locked;
              const isCompleted = mod.progress?.is_completed;
              
              return (
                <button
                  key={mod.module_id}
                  onClick={() => !isLocked && onModuleChange(mod, index)}
                  disabled={isLocked}
                  className={`w-full text-left p-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-[#d4a33a]/20 border border-[#d4a33a]'
                      : isLocked
                      ? 'bg-[#1a1f2e]/50 opacity-50 cursor-not-allowed'
                      : 'bg-[#1a1f2e] hover:bg-[#2a3142]'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isCompleted ? 'bg-green-500' : isActive ? 'bg-[#d4a33a]' : 'bg-[#3a4152]'
                    }`}>
                      {isLocked ? (
                        <Lock className="w-4 h-4 text-white" />
                      ) : isCompleted ? (
                        <Check className="w-4 h-4 text-white" />
                      ) : (
                        <span className="text-white text-sm font-medium">{index + 1}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium truncate ${
                        isActive ? 'text-[#d4a33a]' : 'text-white'
                      }`}>
                        {mod.title}
                      </p>
                      <p className="text-gray-400 text-xs mt-1">Part {mod.part}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
